import {createAsyncThunk} from '@reduxjs/toolkit';
import type {RootState} from '@/store';
import type {TicketingOrderSummary, TicketingOrdersBatch} from '@Components/event-ticketing/event-ticketing.types';
import {LoadingStates} from '@Utils/loading.util';
import {getActiveDonationsList} from './event-donations.helper';
import type {AddTicketingDonorArgs, FetchTicketingDonationsArgs} from './event-donations.types';

/**
 * One page of the Donations tab: the event's orders that still hold a donation, optionally searched. The reducer
 * routes the page into the cached unfiltered list or the fresh searched list; the ticketing slice merges the rows
 * into its shared orders hashmap.
 */
export const fetchTicketingDonations = createAsyncThunk<TicketingOrdersBatch, FetchTicketingDonationsArgs>(
  'eventDonations/fetchDonations',
  ({eventHashedID, currentPageNumber, search}: FetchTicketingDonationsArgs) => {
    return window.PMW.readLocal('eventticketing/getTicketingDonations', {
      event_hid: eventHashedID,
      currentPageNumber,
      search: search === '' ? undefined : search,
    });
  },
  {
    // A second lazy-load effect firing in the same commit would request the same page again; the first dispatch
    // flips the active list to LOADING, so the duplicate is cancelled here.
    condition: ({search}, {getState}) => {
      const {eventDonations} = getState() as RootState;
      return getActiveDonationsList(eventDonations, search).loadingState !== LoadingStates.LOADING;
    },
  }
);

/**
 * Records a donation by hand (Add donor) and returns the new order's row summary: one cash order of a single
 * ticket/registration plus the donation line. The donations slice puts it at the top of the list; the ticketing
 * slice stores the row and puts it at the top of the orders table too, since the donor is also an attendee.
 */
export const addTicketingDonor = createAsyncThunk<TicketingOrderSummary, AddTicketingDonorArgs>(
  'eventDonations/addDonor',
  ({eventHashedID, name, email, amount, sendConfirmationEmail}: AddTicketingDonorArgs) => {
    return window.PMW.writeLocal('eventticketing/addTicketingDonor', {
      event_hid: eventHashedID,
      name,
      email,
      amount,
      sendConfirmationEmail: sendConfirmationEmail ? '1' : undefined,
    });
  }
);
